import {
  ArtButton,
  ArtDialog,
  ArtTable,
  ArtTableHandle,
  confirmDialog,
  FormFieldConsolidatedProps,
  useApiService,
} from "@art/shared-ui";
import { HeadCell } from "@art/shared-ui";
import { SSRTableBuilder } from "@art/shared-ui";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useCallback, useEffect, useRef, useState } from "react";
import ArtClientAddPage from "./add-client-dialog";
import { usePageWrapper } from "../../../hooks";
import ArtClientRoleAssignDialog from "./assign-roles-dialog";
import { Box, LinearProgress } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { AttributePermissionGate, useAttributePermission } from "../../permissions";
import "../application-users/user.css"
import { tableParamNav } from "../../../utils/table-utils";

interface Data {
  id: string;
  client_id: string;
  title: string;
  is_expired: boolean;
  expiry: string;
  role: string;
}

const headCells: HeadCell<Data>[] = [
  {
    id: "title",
    numeric: false,
    disablePadding: false,
    label: "Title",
    getColData: (row) => row["title"],
  },
  {
    id: "client_id",
    numeric: false,
    disablePadding: false,
    label: "Client ID",
    getColData: (row) => row["client_id"],
  },
  {
    id: "role",
    numeric: false,
    disablePadding: false,
    label: "Roles",
    getColData: (row: any) =>
      (row["service_user"]?.["roles"] ?? []).map((x: any) => x.name).join(", "),
  },
  {
    id: "expiry",
    numeric: false,
    disablePadding: false,
    label: "Expiry",
    getColData: (row: any) => {
      const date = row["expiry"]
        ? dayjs(row["expiry"]).format("DD-MM-YYYY")
        : "-";
      const expired = row["is_expired"];

      return <span style={{ color: expired ? "red" : "inherit" }}>{date}</span>;
    },
  },
];

function ApplicationClientSettings() {
  const tableRef = useRef<ArtTableHandle>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [masterRoles, setMasterRoles] = useState([]);
  const [selectDialogOpen, setSelectDialogOpen] = useState(false);
  const [selectedData, setSelectedData]: [Data | null, any] = useState(null);
  const apiService = useApiService();
  const [formKey, setFormKey] = useState(0);
  const [queryParams, setQueryParams]: any = useSearchParams();
  const canDeleteClients = useAttributePermission("PLATFORM_SETTINGS", "CLIENT_MANAGEMENT", "DeleteClients");
  const canAssignClientRoles = useAttributePermission("PLATFORM_SETTINGS", "CLIENT_MANAGEMENT", "AssignClientRoles");
  const tableBuilder = SSRTableBuilder<Data>({
    endpoint: "/client/configuration",
    method: "get",
    apiService: apiService,

    onFetch: (params) => {
      let currentPage = params["page"] || "1";
      let searchText = params["search_text"] || ""; // search
      let roleSearch = params["role"] || "";

      //page and search added to params
      const newParams: any = {
        page: currentPage,
      };
      if (searchText) {
        newParams.search = searchText;
      }
      if (roleSearch) {
        newParams.role = roleSearch;
      }
      setQueryParams(newParams, tableParamNav(queryParams, "page", currentPage)); // Update this

      return {
        page: currentPage,
        search_text: searchText,
        role: roleSearch,
      };
      // return {
      //     "page": params["page"],
      //     "search_text": params["search_text"],
      //     "role": params["role"],
      // }
    },
    onResponse: ({ data }) => {
      return {
        data: data.data ?? [],
        total: data?.total_count,
      };
    },
  });

  useEffect(() => {
    apiService.get("/roles").then(({ data: { data } }) => {
      setMasterRoles(data.roles);
    });
  }, []);

  const openAddClientDialog = (data?: Data) => {
    setSelectedData(data);
    setAddDialogOpen(true);
  };
  const closeAddClientDialog = () => {
    setSelectedData(null);
    setAddDialogOpen(false);
    tableRef.current?.refresh();
  };

  const openAssignRoleDialog = (data?: Data) => {
    setSelectedData(data);
    setSelectDialogOpen(true);
  };
  const closeAssignRoleDialog = () => {
    setSelectedData(null);
    setSelectDialogOpen(false);
    tableRef.current?.refresh();
  };

  const filterBuilder = useCallback(() => {
    return [
      {
        id: "role",
        type: "select",
        label: "Role",
        getOptions: async () => masterRoles,
        getOptionLabel: (data: any) => data["name"],
        getOptionValue: (data: any) => data["name"],
        marginBottom: 14,
      },
      {
        type: "row",
        classNames: [""],
        children: [
          {
            type: "col",
            classNames: ["col-auto"],
            children: [
              {
                type: "submit-button",
                label: "Filter",
              },
            ],
          },
          {
            type: "col",
            classNames: ["col-auto"],
            children: [
              {
                type: "reset-button",
                label: "Reset",
                onClick: () => {
                  setFormKey((prev) => prev + 1);
                },
              },
            ],
          },
        ],
      },
    ] as FormFieldConsolidatedProps[];
  }, [masterRoles]);

  if (!masterRoles.length) {
    return <LinearProgress sx={{ margin: "-20px" }} />;
  }

  return (
    <>
      <ArtDialog
        size="large"
        title="Modify Roles"
        open={selectDialogOpen && !addDialogOpen}
        onClose={closeAssignRoleDialog}
      >
        <ArtClientRoleAssignDialog
          data={selectedData}
          masterRoles={masterRoles}
          closeDialog={closeAssignRoleDialog}
        ></ArtClientRoleAssignDialog>
      </ArtDialog>
      <ArtDialog
        title="Client Credentials"
        open={addDialogOpen && !selectDialogOpen}
        onClose={closeAddClientDialog}
      >
        <ArtClientAddPage closeDialog={closeAddClientDialog} />
      </ArtDialog>
      <Box className="non-clickable-table">
        <ArtTable
          key={formKey}
          ref={tableRef}
          headCells={headCells}
          builder={tableBuilder}
          // search={{ enabled: true, value: "" }}
          search={{
            enabled: true,
            value: queryParams.get("search") || "", // check and add the search text from params
          }}
          pagination={{
            initialPage: parseInt(queryParams.get("page") || "1") - 1,
          }}
          filter={{
            enabled: true,
            builder: filterBuilder,
          }}
          toolbarActions={[
            <AttributePermissionGate application="PLATFORM_SETTINGS" feature="CLIENT_MANAGEMENT" attribute="CreateClients">
              <ArtButton
                variant="contained"
                dataQa="btn-add"
                label="Generate Key"
                icon={faPlus}
                onClick={async () => {
                  openAddClientDialog();
                }}
              />
            </AttributePermissionGate>,
          ]}
          rowActions={([
            canAssignClientRoles && {
              icon: faPlus,
              label: "Assign Roles",
              onClick: async (row: Data) => {
                openAssignRoleDialog(row);
              },
            },
            canDeleteClients && {
              icon: faTrash,
              label: "Delete",
              onClick: (row: Data) => {
                confirmDialog("confirm", "Do you want to delete?").then(() => {
                  apiService.deleteData(
                    `/client/configuration/${row.client_id}`,
                    {
                      onSuccess: () => {
                        tableRef.current?.refresh(0);
                      },
                      onError: (err: any) => {
                        console.log(err);
                      },
                    }
                  )();
                });
              },
            },
          ].filter(Boolean) as any)}
        />
      </Box>

    </>
  );
}

export default usePageWrapper(ApplicationClientSettings, {
  page_title: "Client",
});
